import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { NGXLogger } from "ngx-logger";
import { Adapter } from "src/app/core/adapter";
import { Model } from "src/app/core/models/model";
import {
  Threshold,
  ThresholdAdapter,
} from "src/app/core/models/threshold.model";
import { UtilsService } from "src/app/shared/services/utils.service";
import { User } from "./user.model";

export enum ShowerHeadType {
  aloe = "aloe",
  yucca = "yucca",
  first = "first",
  origin = "origin",
  cereus = "cereus",
  unknown = "unknown",
}

export enum ShowerHeadConnectivity {
  ble = "ble",
  lora = "lora",
}

export interface ShowerheadData {
  placeLabel: string;
  gatewayLabel: string;
}
export class ShowerHead implements Model {
  // ATTENTION for each custom props => add to clone method too !!!!

  public placeLabel: string = null;
  public gatewayLabel: string = null;

  constructor(
    public device_uuid: string,
    public first_seen: DateTime,
    public last_seen: DateTime,
    public threshold: Threshold[],
    public threshold_request: Threshold[],
    public label: string,
    public serial: string,
    public type: string,
    public connectivity: string,
    public hw_version: string,
    public fw_version: string,
    public place_id: number,
    public gateway_uuid: string
  ) {}

  public get lastSeenDuration(): number {
    if (!this.last_seen || this.last_seen === null) return 0;

    return (
      (DateTime.local().toMillis() - this.last_seen.toMillis()) / 1000 / 60
    );
  }

  public get lastSeenTooltip() {
    let hours = Math.round(this.lastSeenDuration / 60);
    let minutes = ("0" + (this.lastSeenDuration - 60 * hours)).slice(-2);
    if (this.lastSeenDuration == 0) return "";
    else if (this.lastSeenDuration < 3) {
      return $localize`seen in the last 3 minutes`;
    } else if (this.lastSeenDuration <= 1440) {
      return $localize`last seen ${hours}:hh: hours ${minutes}:minutes: minutes  ago`;
    } else {
      try {
        return $localize`last seen on ${new Intl.DateTimeFormat(
          $localize.locale || "en",
          {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false, // Pour avoir un format 24h
            timeZone: Settings.defaultZone.name,
          }
        ).format(this.last_seen.toJSDate())}:date:`;
      } catch (err) {
        console.error("FAILED to format last seen", this.last_seen);
        return "";
      }
    }
  }

  public lastSeenRelative(locale: string) {
    return this.last_seen !== null
      ? this.last_seen.setLocale(locale).toRelative()
      : $localize`never seen`;
  }

  public get typeSvgIcon(): string {
    if (this.isYucca) return "yucca";
    else if (this.isCereus) return "cereus";
    else if (this.isFirst) return "first";
    else if (this.isAloe) return "aloe";
    else return null;
  }

  public get thresholdPending(): boolean {
    if (!this.threshold && this.threshold_request) return true;
    if (
      this.threshold &&
      this.threshold_request &&
      Array.isArray(this.threshold) &&
      Array.isArray(this.threshold_request)
    ) {
      let sortedThreshold = this.threshold.sort(
        (t1, t2) => t1.liter - t2.liter
      );
      let sortedThresholdRequest = this.threshold_request.sort(
        (t1, t2) => t1.liter - t2.liter
      );

      for (let i = 0; i < sortedThreshold.length; i++) {
        if (!sortedThreshold[i].equals(sortedThresholdRequest[i])) {
          return true;
        }
      }
      return false;
    }
    return false;
  }

  public get isBLE(): boolean {
    return this.connectivity?.toLowerCase() == "ble";
  }

  public get isLoRa(): boolean {
    return this.connectivity?.toLowerCase() == "lora";
  }

  public get isAloe(): boolean {
    return this.type?.toLowerCase() == "aloe";
  }
  public get isYucca(): boolean {
    return this.type?.toLowerCase() == "yucca";
  }
  public get isFirst(): boolean {
    return (
      this.type?.toLowerCase() == "first" ||
      this.type?.toLowerCase() == "origin"
    );
  }
  public get isCereus(): boolean {
    return this.type?.toLowerCase() == "cereus";
  }

  public get isTypeUnknown(): boolean {
    return this.type?.toLowerCase() == "unknown" || !this.type;
  }

  public get universalLabel(): string {
    if (this.label && this.label.trim() !== "") return this.label;
    if (this.serial && this.serial.trim() !== "") return this.serial;
    return this.device_uuid;
  }

  public prepareThresholds(utils: UtilsService, user: User) {
    if (this.threshold) {
      this.threshold.map((threshold) => {
        threshold.literWithUnit = utils.getVolumeFormattedWithUser(
          user,
          threshold.liter
        );
      });
    }
    if (this.threshold_request) {
      this.threshold_request.map((threshold) => {
        threshold.literWithUnit = utils.getVolumeFormattedWithUser(
          user,
          threshold.liter
        );
      });
    }
  }

  public clone() {
    const clone = new ShowerHead(
      this.device_uuid,
      this.first_seen,
      this.last_seen,
      Array.isArray(this.threshold) ? [...this.threshold] : null,
      Array.isArray(this.threshold_request)
        ? [...this.threshold_request]
        : null,
      this.label,
      this.serial,
      this.type,
      this.connectivity,
      this.hw_version,
      this.fw_version,
      this.place_id,
      this.gateway_uuid
    );

    clone.placeLabel = this.placeLabel;
    clone.gatewayLabel = this.gatewayLabel;

    return clone;
  }

  public setData(data: ShowerheadData) {
    if (!data) return;

    this.placeLabel = data.placeLabel;
    this.gatewayLabel = data.gatewayLabel;
  }
}

@Injectable({
  providedIn: "root",
})
export class ShowerHeadAdapter implements Adapter<ShowerHead> {
  constructor(
    private thresholdAdapter: ThresholdAdapter,
    private logger: NGXLogger
  ) {}

  adapt(item: any): ShowerHead {
    let threshold = null;
    let threshold_request = null;
    try {
      threshold = JSON.parse(item.threshold);
    } catch (e) {
      // this.logger.error("wrong JSON for threshold on sh " + item.device_uuid);
    }
    try {
      threshold_request = JSON.parse(item.threshold_request);
    } catch (e) {
      // this.logger.error(
      //   "wrong JSON for threshold request on sh " + item.device_uuid
      // );
    }

    return new ShowerHead(
      item.device_uuid,
      item.first_seen !== null
        ? DateTime.fromISO(item.first_seen, { zone: Settings.defaultZone })
        : null,
      item.last_seen !== null
        ? DateTime.fromISO(item.last_seen, { zone: Settings.defaultZone })
        : null,
      Array.isArray(threshold)
        ? threshold.map((i) => this.thresholdAdapter.adapt(i))
        : null,
      Array.isArray(threshold_request)
        ? threshold_request.map((i) => this.thresholdAdapter.adapt(i))
        : null,
      item.label,
      item.serial,
      item.type,
      item.connectivity,
      item.hw_version,
      item.fw_version,
      item.place_id,
      item.gateway_uuid
    );
  }
}
